import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, Phone, MapPin, Clock, X } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { useSite, telLink } from "@/context/SiteContext";

export default function Header() {
  const s = useSite();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50" data-testid="site-header">
      {/* top strip */}
      <div className="hidden md:block bg-navy text-white text-xs">
        <div className="container-x flex items-center justify-between py-2">
          <div className="flex items-center gap-5">
            <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-brand-sky" /> Jodhpur, Rajasthan</span>
            <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-brand-sky" /> Available 24/7</span>
          </div>
          <a href={telLink(s)} className="flex items-center gap-1.5 hover:text-brand-sky transition-colors" data-testid="topbar-call-link">
            <Phone className="w-3.5 h-3.5 text-brand-sky" /> {s.phone}
          </a>
        </div>
      </div>

      {/* main bar */}
      <div className="bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="container-x flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo-link">
            <img src={s.logo} alt="Chouhan Taxi Service logo" className="h-12 w-12 rounded-full object-cover ring-1 ring-slate-200" />
            <div className="leading-tight">
              <div className="font-heading font-extrabold text-navy-heading text-lg tracking-tight">Chouhan Taxi Service</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand font-bold">Tour & Travel</div>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-0.5" data-testid="desktop-nav">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} data-testid={`nav-${l.name.toLowerCase().replace(/ /g, "-")}-link`}
                className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-brand rounded-lg hover:bg-brand-light transition-colors">
                {l.name}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link to="/book-taxi" data-testid="header-book-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-brand hover:bg-royal text-white font-semibold text-sm px-5 py-2.5 rounded-full transition-all active:scale-95 shadow-md shadow-brand/20">
              <Phone className="w-4 h-4" /> Book Taxi
            </Link>
            <button onClick={() => setOpen(true)} className="lg:hidden p-2 text-navy" aria-label="Open menu" data-testid="mobile-menu-toggle">
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>

      {/* mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[60] lg:hidden" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-navy/60" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-white shadow-2xl p-5 overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <img src={s.logo} alt="Chouhan Taxi Service" className="h-10 w-10 rounded-full object-cover" />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close"><X className="w-6 h-6 text-navy" /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <Link key={l.to} to={l.to} className="px-3 py-3 rounded-lg text-navy-heading font-medium hover:bg-brand-light border-b border-slate-100">
                  {l.name}
                </Link>
              ))}
            </nav>
            <Link to="/book-taxi" className="mt-5 flex items-center justify-center gap-2 bg-brand text-white font-semibold px-5 py-3 rounded-full">
              <Phone className="w-4 h-4" /> Book a Taxi
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
